import React, { useState, useEffect, useRef, useCallback } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { apiFetch } from '../App';
import { Radio, Loader2, AlertCircle, RefreshCw, X, Volume2, VolumeX } from 'lucide-react';

AgoraRTC.setLogLevel(3);

export default function LiveStreamViewer({ booking, onClose }) {
  const [status, setStatus] = useState('connecting'); // connecting | watching | no-stream | error | ended
  const [errorMsg, setErrorMsg] = useState('');
  const [hasVideo, setHasVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [viewerCount, setViewerCount] = useState(1);

  const clientRef = useRef(null);
  const videoElRef = useRef(null);
  const remoteUsersRef = useRef({});
  const pollIntervalRef = useRef(null);

  const cleanup = useCallback(async () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (clientRef.current) {
      try {
        clientRef.current.off('user-published');
        clientRef.current.off('user-unpublished');
        clientRef.current.off('user-left');
        if (clientRef.current.connectionState !== 'DISCONNECTED') {
          await clientRef.current.leave();
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    connect();
    return () => { cleanup(); };
  }, []);

  const connect = async () => {
    try {
      setStatus('connecting');
      setErrorMsg('');

      // Check if stream is currently live
      const bookingData = await apiFetch(`/api/bookings/${booking.id}`);
      if (bookingData.liveStreamStatus !== 'LIVE') {
        setStatus('no-stream');
        startPolling();
        return;
      }

      // Get viewer token
      const tokenData = await apiFetch('/api/agora/token', {
        method: 'POST',
        body: JSON.stringify({ bookingId: booking.id, role: 'audience' })
      });

      // Create Agora client in live broadcast mode
      clientRef.current = AgoraRTC.createClient({ mode: 'live', codec: 'h264' });
      await clientRef.current.setClientRole('audience');

      // Listen for remote stream published
      clientRef.current.on('user-published', async (user, mediaType) => {
        await clientRef.current.subscribe(user, mediaType);
        if (mediaType === 'video') {
          remoteUsersRef.current[user.uid] = user;
          if (videoElRef.current) {
            user.videoTrack.play(videoElRef.current);
            setHasVideo(true);
          }
        }
        if (mediaType === 'audio') {
          if (!isMuted) user.audioTrack.play();
        }
        setViewerCount(prev => prev + 1);
      });

      clientRef.current.on('user-unpublished', (user, mediaType) => {
        if (mediaType === 'video') setHasVideo(false);
      });

      clientRef.current.on('user-left', () => {
        setHasVideo(false);
        setStatus('ended');
      });

      // Join channel
      await clientRef.current.join(
        tokenData.appId,
        tokenData.channel,
        tokenData.token,
        tokenData.uid
      );

      setStatus('watching');
      startPolling();
    } catch (err) {
      console.error('Viewer connect error:', err);
      setErrorMsg(err.message || 'Failed to connect to stream');
      setStatus('error');
    }
  };

  const startPolling = () => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const bookingData = await apiFetch(`/api/bookings/${booking.id}`);
        if (bookingData.liveStreamStatus === 'LIVE' && status === 'no-stream') {
          clearInterval(pollIntervalRef.current);
          connect();
        } else if (bookingData.liveStreamStatus !== 'LIVE' && status === 'watching') {
          setStatus('ended');
          setHasVideo(false);
        }
      } catch {}
    }, 5000);
  };

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    Object.values(remoteUsersRef.current).forEach(user => {
      if (user.audioTrack) {
        if (newMuted) user.audioTrack.stop();
        else user.audioTrack.play();
      }
    });
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0a0a0a', border: '1px solid var(--border-color)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-red-400" />
          <span className="font-outfit font-bold text-sm text-white">Live Grooming</span>
          {status === 'watching' && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#EF4444', color: 'white' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              LIVE
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-all">
          <X size={16} />
        </button>
      </div>

      {/* Video Area */}
      <div className="relative bg-black" style={{ aspectRatio: '16/9', minHeight: 180 }}>
        <div ref={videoElRef} className="w-full h-full" />

        {status === 'connecting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Loader2 size={32} className="text-petfolk-orange animate-spin" />
            <p className="text-white/60 text-sm">Connecting to stream...</p>
          </div>
        )}

        {status === 'no-stream' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.2)' }}>
              <Radio size={24} className="text-petfolk-purple" />
            </div>
            <p className="text-white/70 text-sm font-semibold">Stream not started yet</p>
            <p className="text-white/40 text-xs text-center">Waiting for your groomer to start the live session...</p>
            <div className="flex items-center gap-2 text-white/40 text-xs">
              <Loader2 size={12} className="animate-spin" />
              Checking every 5 seconds
            </div>
          </div>
        )}

        {status === 'watching' && !hasVideo && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Loader2 size={28} className="text-white/50 animate-spin" />
            <p className="text-white/50 text-sm">Loading video...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
            <AlertCircle size={32} className="text-red-400" />
            <p className="text-red-300 text-sm text-center">{errorMsg}</p>
            <button
              onClick={connect}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <RefreshCw size={12} />
              Retry
            </button>
          </div>
        )}

        {status === 'ended' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <Radio size={24} className="text-white/40" />
            </div>
            <p className="text-white/60 text-sm">Stream ended</p>
            <p className="text-white/40 text-xs">Your grooming session is complete</p>
          </div>
        )}

        {/* Viewer count overlay (only when watching) */}
        {status === 'watching' && hasVideo && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>
            👁 Watching live
          </div>
        )}
      </div>

      {/* Controls footer */}
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <p className="text-white/40 text-xs">
          {status === 'watching' ? '🐾 ' + (booking.pet?.name || 'Your pet') + "'s grooming session" : status === 'no-stream' ? 'Session not started' : status === 'ended' ? 'Session completed' : ''}
        </p>
        {status === 'watching' && (
          <button
            onClick={toggleMute}
            className="p-1.5 rounded-lg transition-all"
            style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}
          >
            {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
